import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react"

import { Auth, API } from "aws-amplify"
import qs from "query-string"
import { useSnackbar } from "notistack"

import { useEsriToken } from "./useEsriToken"
import { useEsriModules } from "./useEsriModules"

import { PORTAL_URL, ESRI_CLIENT_ID } from "./constants"
import {
  handleRegisterEsriToken,
  buildOpenIDToken,
  getRefreshToken,
} from "./authHelpers"
import { useLocation } from "react-router-dom"

type AuthStatus = "signedOut" | "signedIn" | "loading"

/**
 * Documentation & help
 * https://developers.arcgis.com/documentation/core-concepts/security-and-authentication/server-based-user-logins/
 * https://developers.arcgis.com/javascript/latest/api-reference/esri-identity-IdentityManager.html#initialize
 * https://community.esri.com/thread/119908
 */

/** Type return values */
interface AuthContextValue {
  status: AuthStatus
  requestAuthChange: (status: Omit<AuthStatus, "loading">) => void
  username: string | undefined
}

/** Generate the context */
const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/** Provider to wrap your app with */
export const AuthProvider: React.FC = ({ children }) => {
  /** Store login status */
  const [status, setStatus] = useState<AuthStatus>("loading")

  /** Get snackbar */
  const { enqueueSnackbar } = useSnackbar()

  /** On successful login */
  const onSuccess = useCallback(() => {
    setStatus("signedIn")
    enqueueSnackbar("Signed in", {
      key: "SIGNED_IN_SUCCESS",
      preventDuplicate: true,
    })
  }, [enqueueSnackbar])

  /** On login error */
  const onError = useCallback(
    (error: Error) => {
      console.log("Error signing in:", error)
      setStatus("signedOut")
      enqueueSnackbar("Error logging you in", { variant: "error" })
    },
    [enqueueSnackbar]
  )

  /** Grab the token from storage */
  const [esriToken, setEsriToken] = useEsriToken()

  /** Store username in state */
  const [username, setUsername] = useState<string | undefined>()

  /** Check signin status and register if there's a stored token */
  const [IdentityManager] = useEsriModules<[__esri.IdentityManager]>([
    "esri/identity/IdentityManager",
  ])
  const checkSignInStatus = useCallback(async () => {
    if (esriToken && IdentityManager) {
      /** Load in the stored token */
      IdentityManager.registerToken(esriToken)

      /** Check whether the token is valid and log them in. */
      IdentityManager.checkSignInStatus(PORTAL_URL + "/sharing")
        .then(() => {
          setUsername(esriToken.userId)
          onSuccess()
        })
        .catch(() => {
          setUsername(undefined)
          setStatus("signedOut")
        })
    } else {
      setStatus("signedOut")
    }
  }, [esriToken, IdentityManager, onSuccess])

  /** On mount, check for a code and set it */
  const { search } = useLocation()
  useEffect(() => {
    /** Check if esri has loaded... */
    if (!IdentityManager) return

    /** Check if there's a token and log it in */
    checkSignInStatus()

    /** Check for return code to authorize */
    if (search.includes("code")) {
      setStatus("loading")
      /** Grab the code from the search params */
      const { code } = qs.parse(search)

      /**
       * If there's a code in the URL, grab it and exchange
       * it for an auth token (plus get a openID token from aws)
       */
      API.get("featureeditorstorageapi", "/token", {
        queryStringParameters: {
          code,
          grant_type: "token",
          redirect_uri: window.location.origin,
        },
      })
        .then(({ esriResponse, oidToken }) => {
          /** Register the esri token */
          const tokenToStore = handleRegisterEsriToken(
            esriResponse,
            IdentityManager
          )

          /** Store it in localStorage */
          setEsriToken(tokenToStore)

          /** Set the username */
          setUsername(esriResponse.username)

          /** Sign in the AWS Auth client with our identity */
          return Auth.signOut().then(() =>
            Auth.federatedSignIn("developer", buildOpenIDToken(oidToken), {
              name: esriResponse.username,
            })
          )
        })
        .then(onSuccess)
        .catch(error => {
          console.log("error getting tokens from code", error)
          onError(error)
        })
    }
  }, [
    IdentityManager,
    checkSignInStatus,
    onError,
    onSuccess,
    setEsriToken,
    search,
  ])

  /** Track timer for refresh token */
  const timeoutRef = useRef()
  const cancelRefreshTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  /** Setup new timeout to refresh the token */
  useEffect(() => {
    /** return early if there's no refresh token */
    if (!esriToken || !esriToken.refresh_token || !IdentityManager) return

    /** Cancel any previously existing timeout */
    cancelRefreshTimeout()

    /** Start a new timer to get the refresh token when it expires */
    setTimeout(() => {
      getRefreshToken(esriToken, IdentityManager)
        .then(({ esriResponse, oidToken }) => {
          /** Register the esri token */
          const tokenToStore = handleRegisterEsriToken(
            esriResponse,
            IdentityManager
          )

          /** Store it in localStorage */
          setEsriToken(tokenToStore)

          /** Update the AWS credentials with the new token */
          if (esriToken.userId) {
            Auth.federatedSignIn("developer", buildOpenIDToken(oidToken), {
              name: esriToken.userId,
            })
          }
        })
        .catch(() => setEsriToken(undefined))
    }, Date.now() - esriToken.expires_at)
  }, [esriToken, setEsriToken, IdentityManager])

  /** Handle configuring developer refresh token */
  useEffect(() => {
    Auth.configure({
      refreshHandlers: {
        developer: () => {
          cancelRefreshTimeout()
          if (!esriToken)
            throw Error(
              "No refresh token to call with when refreshing AWS credentials"
            )
          const { refresh_token } = esriToken
          if (refresh_token && IdentityManager) {
            getRefreshToken(esriToken, IdentityManager)
              .then(({ esriResponse, oidToken }) => {
                /** Register the esri token */
                const tokenToStore = handleRegisterEsriToken(
                  esriResponse,
                  IdentityManager
                )

                /** Store it in localStorage */
                setEsriToken(tokenToStore)

                /** Update the AWS credentials with the new token */
                esriToken.userId &&
                  Auth.federatedSignIn(
                    "developer",
                    buildOpenIDToken(oidToken),
                    {
                      name: esriToken.userId,
                    }
                  )
              })
              .catch(() => {
                enqueueSnackbar("Error getting new token, logging out.", {
                  variant: "error",
                })
                // Auth.signOut().then(() => setStatus("signedOut"));
              })
          }
        },
      },
    })
  }, [esriToken, IdentityManager, enqueueSnackbar, setEsriToken])

  /** Handle sign in/out via click */
  const requestAuthChange = (action: Omit<AuthStatus, "loading">) => {
    if (action === "signIn") {
      window.location.href =
        PORTAL_URL +
        "/sharing/rest/oauth2/authorize?" +
        qs.stringify({
          client_id: ESRI_CLIENT_ID,
          response_type: "code",
          expiration: 20160,
          redirect_uri: window.location.origin,
        })
    } else {
      IdentityManager?.destroyCredentials()
      setUsername(undefined)
      setEsriToken(undefined)
      Auth.signOut().then(() => setStatus("signedOut"))
    }
  }

  return (
    <AuthContext.Provider value={{ status, requestAuthChange, username }}>
      {children}
    </AuthContext.Provider>
  )
}

/** Hook to consume the context */
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw Error("Not inside <AuthProvider />")
  return context
}
